import { Modal, ModalBody, ModalContent, ModalFooter } from '@heroui/react';
import { useEffect, useState, useRef } from 'react';
import CustomButton from '../../components/CustomButton';
import {
  FaStar,
  FaRegStar,
  FaImage,
  FaChevronLeft,
  FaChevronRight,
  FaBriefcase,
  FaLocationDot,
  FaAlignLeft,
  FaCalendar,
  FaMedal,
} from 'react-icons/fa6';
import { resolveStorageUrl } from '../Customer/aws/s3FileUpload';

const REVIEW_IMAGES_FOLDER = 'review-images';

// Add default image URL
const DEFAULT_IMAGE_URL =
  'https://cdn.hirepanther.ca/common/default-fallback-image.png';

// Keep FALLBACK_IMAGE as a backup for failed thumbnails
const FALLBACK_IMAGE =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAiIGhlaWdodD0iODAiIGZpbGw9IiNFNUU3RUIiLz48L3N2Zz4=';

const ImageSkeleton = () => {
  return (
    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer rounded-2xl" />
  );
};

const ImageLoadingOverlay = ({ isLoading }: { isLoading: boolean }) => {
  if (!isLoading) return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-xs z-20">
      <div className="flex flex-col items-center gap-2">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
        <span className="text-xs text-gray-600 font-medium">Loading</span>
      </div>
    </div>
  );
};

interface ViewJobDetailsProps {
  isOpen: boolean;
  onOpenChange: () => void;
  job: any;
}

const ViewJobDetails = ({ isOpen, onOpenChange, job }: ViewJobDetailsProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const [imageError, setImageError] = useState<boolean>(false);
  const [thumbnailsLoaded, setThumbnailsLoaded] = useState<Set<string>>(
    new Set()
  );
  const imgRef = useRef<HTMLImageElement>(null);

  const getJobImages = (item: any): string[] => {
    if (!item) return [];

    const imageUrls: string[] = [];
    const source =
      Array.isArray(item.selectedImages) && item.selectedImages.length > 0
        ? item.selectedImages
        : Array.isArray(item.images)
          ? item.images
          : [];

    source.forEach((img: any) => {
      const rawUrl =
        typeof img === 'string'
          ? img
          : img?.imageUrl || img?.url || img?.fullUrl || img?.src || '';
      if (rawUrl) {
        const resolved = resolveStorageUrl(rawUrl, REVIEW_IMAGES_FOLDER);
        if (resolved && !imageUrls.includes(resolved)) {
          imageUrls.push(resolved);
        }
      }
    });

    // If no images found, return default image
    if (imageUrls.length === 0) {
      return [DEFAULT_IMAGE_URL];
    }

    return imageUrls;
  };

  const jobImages = getJobImages(job);
  const imageCount = jobImages.length;
  const currentImage =
    imageCount > 0 ? jobImages[currentImageIndex] : DEFAULT_IMAGE_URL;

  // Reset state when image changes
  useEffect(() => {
    setImageLoading(true);
    setImageError(false);

    // Preload the image
    if (currentImage) {
      const img = new Image();
      img.onload = () => {
        setImageLoading(false);
        setImageError(false);
      };
      img.onerror = () => {
        // If image fails and it's not the default image, try to use default
        if (currentImage !== DEFAULT_IMAGE_URL) {
          // Try fallback through resolveStorageUrl
          const fileName = currentImage.split('/').pop();
          if (fileName) {
            const fallbackResolved = resolveStorageUrl(
              fileName,
              REVIEW_IMAGES_FOLDER
            );
            if (fallbackResolved && fallbackResolved !== currentImage) {
              img.src = fallbackResolved;
              img.onload = () => {
                setImageLoading(false);
                setImageError(false);
                // Update the image ref with fallback
                if (imgRef.current) {
                  imgRef.current.src = fallbackResolved;
                }
              };
              img.onerror = () => {
                // If fallback also fails, use default image
                if (imgRef.current) {
                  imgRef.current.src = DEFAULT_IMAGE_URL;
                  setImageLoading(false);
                  setImageError(false);
                }
              };
              return;
            }
          }
          // If no fallback, use default image
          if (imgRef.current) {
            imgRef.current.src = DEFAULT_IMAGE_URL;
            setImageLoading(false);
            setImageError(false);
          }
        } else {
          // Default image failed, show error state
          setImageLoading(false);
          setImageError(true);
        }
      };
      img.src = currentImage;
    } else {
      setImageLoading(false);
    }
  }, [currentImageIndex, currentImage]);

  const goToPreviousImage = () => {
    if (imageCount > 0) {
      setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : imageCount - 1));
    }
  };

  const goToNextImage = () => {
    if (imageCount > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % imageCount);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    // If current image is already the default, show error state
    if (currentImage === DEFAULT_IMAGE_URL) {
      setImageLoading(false);
      setImageError(true);
      return;
    }

    // Try fallback through resolveStorageUrl
    const fileName = currentImage?.split('/').pop();
    if (fileName && imgRef.current) {
      const fallbackResolved = resolveStorageUrl(
        fileName,
        REVIEW_IMAGES_FOLDER
      );
      if (fallbackResolved && fallbackResolved !== currentImage) {
        imgRef.current.src = fallbackResolved;
        imgRef.current.onload = () => {
          setImageLoading(false);
          setImageError(false);
        };
        imgRef.current.onerror = () => {
          // If fallback also fails, use default image
          imgRef.current!.src = DEFAULT_IMAGE_URL;
          setImageLoading(false);
          setImageError(false);
        };
        return;
      }
    }

    // If all fails, use default image
    if (imgRef.current) {
      imgRef.current.src = DEFAULT_IMAGE_URL;
      setImageLoading(false);
      setImageError(false);
    } else {
      setImageLoading(false);
      setImageError(true);
    }
  };

  const handleThumbnailLoad = (imgUrl: string) => {
    setThumbnailsLoaded((prev) => new Set(prev).add(imgUrl));
  };

  const handleThumbnailError = (
    imgUrl: string,
    e: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const target = e.currentTarget;

    // If thumbnail is already the default image, use FALLBACK_IMAGE
    if (imgUrl === DEFAULT_IMAGE_URL) {
      target.src = FALLBACK_IMAGE;
      return;
    }

    // Try fallback for thumbnail
    const fileName = imgUrl.split('/').pop();
    if (fileName) {
      const fallbackResolved = resolveStorageUrl(
        fileName,
        REVIEW_IMAGES_FOLDER
      );
      if (fallbackResolved && fallbackResolved !== imgUrl) {
        target.src = fallbackResolved;
        return;
      }
    }
    // If all fails, use default image
    target.src = DEFAULT_IMAGE_URL;
  };

  const getRatingValue = (item: any): number => {
    if (item?.ratingId) {
      const ratingNum = Number(item.ratingId);
      if (!isNaN(ratingNum) && ratingNum > 0) {
        return ratingNum;
      }
    }
    return item?.rating || 0;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const ratingValue = getRatingValue(job);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="2xl"
      scrollBehavior="normal"
      hideCloseButton={true}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <style>{`
              @keyframes shimmer {
                0% { background-position: -200% 0; }
                100% { background-position: calc(200% + 20px) 0; }
              }
              .animate-shimmer { animation: shimmer 2s infinite; }
              @media (prefers-reduced-motion: reduce) {
                .animate-shimmer { animation: none; background: #f0f0f0; }
              }
            `}</style>

            <ModalBody className="pt-5 pb-3">
              {job && (
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center text-primary-500 flex-shrink-0">
                      <FaBriefcase size={16} />
                    </div>
                    <h3 className="text-title2 font-bold text-gray-800">
                      {job?.serviceName || 'Project Details'}
                    </h3>
                  </div>

                  {/* Main Image */}
                  <div className="relative w-full h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden bg-gray-900">
                    {currentImage ? (
                      <>
                        {/* Show skeleton while loading */}
                        {imageLoading && (
                          <div className="absolute inset-0 z-10">
                            <ImageSkeleton />
                          </div>
                        )}

                        {/* Loading Overlay */}
                        <ImageLoadingOverlay isLoading={imageLoading} />

                        <img
                          key={`main-${currentImage}-${currentImageIndex}`}
                          ref={imgRef}
                          src={currentImage}
                          alt={job?.serviceName || 'Portfolio image'}
                          className={`w-full h-full object-cover transition-opacity duration-500 ${
                            imageLoading || imageError
                              ? 'opacity-0'
                              : 'opacity-100'
                          }`}
                          loading="lazy"
                          onLoad={handleImageLoad}
                          onError={handleImageError}
                        />

                        {/* Show error state if image failed */}
                        {imageError && !imageLoading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                            <div className="text-center">
                              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-2">
                                <FaImage className="text-gray-400 text-xl" />
                              </div>
                              <p className="text-xs text-gray-400">
                                Failed to load image
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Navigation Buttons - Always show when multiple images, even if current image fails */}
                        {imageCount > 1 && !imageLoading && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                goToPreviousImage();
                              }}
                              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2.5 transition-all duration-200 hover:scale-110 shadow-md z-10"
                              aria-label="Previous image"
                            >
                              <FaChevronLeft size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                goToNextImage();
                              }}
                              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2.5 transition-all duration-200 hover:scale-110 shadow-md z-10"
                              aria-label="Next image"
                            >
                              <FaChevronRight size={14} />
                            </button>
                          </>
                        )}

                        {/* Image Counter */}
                        {imageCount > 1 && !imageLoading && (
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full">
                            <span className="text-white text-xs font-medium">
                              {currentImageIndex + 1} / {imageCount}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <div className="text-center">
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-2">
                            <FaImage className="text-gray-400 text-xl" />
                          </div>
                          <p className="text-xs text-gray-400">No images</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Thumbnails */}
                  {imageCount > 1 && (
                    <div className="flex items-center justify-center gap-2 overflow-x-auto py-1">
                      {jobImages.map((img: string, idx: number) => (
                        <button
                          key={`thumb-${idx}`}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 transition-all duration-200 ${
                            idx === currentImageIndex
                              ? 'ring-2 ring-primary-400 ring-offset-2'
                              : 'opacity-80 hover:opacity-100'
                          }`}
                        >
                          {!thumbnailsLoaded.has(img) && (
                            <div className="absolute inset-0">
                              <ImageSkeleton />
                            </div>
                          )}
                          <img
                            src={img}
                            alt={`Thumbnail ${idx + 1}`}
                            className={`w-full h-full object-cover transition-opacity duration-300 ${
                              thumbnailsLoaded.has(img)
                                ? 'opacity-100'
                                : 'opacity-0'
                            }`}
                            loading="lazy"
                            onLoad={() => handleThumbnailLoad(img)}
                            onError={(e) => handleThumbnailError(img, e)}
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Description - Updated with icon */}
                  {job?.description && (
                    <div className="border border-primary-100 bg-primary-50/20 rounded-xl px-3 py-2 flex items-start gap-2">
                      <FaAlignLeft
                        size={14}
                        className="text-primary flex-shrink-0 mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-800">
                            Description
                          </span>
                        </div>
                        <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                          {job.description}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  {job?.location && (
                    <div className="border border-primary-100 bg-primary-50/20 rounded-xl px-3 py-2 flex items-center gap-2">
                      <FaLocationDot
                        size={14}
                        className="text-primary flex-shrink-0"
                      />
                      <span className="text-sm font-semibold text-gray-800 flex-shrink-0">
                        Service Location
                      </span>
                      <span className="text-gray-300 flex-shrink-0">|</span>
                      <span
                        className="text-xs text-gray-700 truncate"
                        title={job.location}
                      >
                        {job.location}
                      </span>
                    </div>
                  )}

                  {/* Meta Info - Updated with icons */}
                  <div className="grid grid-cols-2 gap-3">
                    {job?.createdAt && (
                      <div className="border border-primary-100 bg-primary-50/20 rounded-xl px-3 py-2 flex items-center gap-2">
                        <FaCalendar
                          size={14}
                          className="text-primary flex-shrink-0"
                        />
                        <span className="text-sm font-semibold text-gray-800 flex-shrink-0">
                          Created At
                        </span>
                        <span className="text-gray-300 flex-shrink-0">|</span>
                        <span className="text-xs text-gray-700 truncate">
                          {formatDate(job.createdAt)}
                        </span>
                      </div>
                    )}

                    {job?.showRating && ratingValue > 0 && (
                      <div className="border border-primary-100 bg-primary-50/20 rounded-xl px-3 py-2 flex items-center gap-2">
                        <FaMedal
                          size={14}
                          className="text-primary flex-shrink-0"
                        />
                        <span className="text-sm font-semibold text-gray-800 flex-shrink-0">
                          Rating
                        </span>
                        <span className="text-gray-300 flex-shrink-0">|</span>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }, (_, i) =>
                            i < Math.round(ratingValue) ? (
                              <FaStar
                                key={i}
                                size={12}
                                className="text-yellow-400 fill-yellow-400"
                              />
                            ) : (
                              <FaRegStar
                                key={i}
                                size={12}
                                className="text-gray-300"
                              />
                            )
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          ({ratingValue.toFixed(1)})
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </ModalBody>
            <ModalFooter className="pt-2">
              <CustomButton
                label="Close"
                color="danger"
                variant="bordered"
                onPress={onClose}
                className="font-medium hover:bg-danger-100"
                size="sm"
              />
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ViewJobDetails;
